import { Controller } from "@hotwired/stimulus"

// Vendored host helpers — see app/javascript/lib/ (no shared-alias imports here).
import { getConsumer } from "../../lib/action-cable/consumer"
import {
  deepLinkFor,
  deepLinkInputFor,
} from "../../lib/notifications/notification-deep-link"
import { formatTimestamp } from "../../lib/notifications/notification-helpers"
import {
  getNotifications,
  markAllAsRead,
  markAsRead,
} from "../../lib/notifications/notification-service"

// ui--notifications — Hotwire twin of the React NotificationsButton (CONEK-4920).
// Hydrates the shell rendered by Ui::NotificationsMenuComponent from the existing
// JSON API (/api/v1/notifications) and keeps the badge live over the existing
// NotificationsChannel. Renders the row list by cloning the server-rendered
// <template>s (rows, per-type icons) so the design-system markup stays in ERB.
//
// The panel exists twice in the DOM (desktop popover + mobile dialog); every
// render writes to ALL matching [data-notif-field] nodes so both surfaces stay
// in sync.
const CHANNEL = "NotificationsChannel"
const INITIAL_TAKE = 20
const PAGE_STEP = 10
const NOTIFICATIONS_CHANNEL_TYPE = "new_notification"

export default class extends Controller {
  static values = {
    pollInterval: { type: Number, default: 300000 },
    labels: { type: Object, default: {} },
  }

  connect() {
    this.activeTab = "all"
    this.takes = { all: INITIAL_TAKE, unread: INITIAL_TAKE }
    this.state = {
      all: { items: [], total: 0 },
      unread: { items: [], total: 0 },
    }
    this.loading = false

    this.#initialFetch()
    this.#subscribe()
    this.#startPoll()
    this.#observeSentinels()
  }

  disconnect() {
    this.subscription?.unsubscribe()
    this.subscription = null
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.observer?.disconnect()
  }

  // ── Data ──────────────────────────────────────────────────────────────────

  async #initialFetch() {
    this.#toggleLoading(true)
    try {
      const [all, unread] = await Promise.all([
        getNotifications({ take: this.takes.all, listType: "all" }),
        getNotifications({ take: this.takes.unread, listType: "unread" }),
      ])
      this.state.all = { items: all.results, total: all.total }
      this.state.unread = { items: unread.results, total: unread.total }
    } catch {
      // A failed fetch leaves the (empty) state — the empty view shows.
    } finally {
      this.#toggleLoading(false)
      this.#render()
    }
  }

  async #refetch(listType = this.activeTab) {
    try {
      const data = await getNotifications({
        take: this.takes[listType],
        listType,
      })
      this.state[listType] = { items: data.results, total: data.total }
    } catch {
      /* keep prior state */
    }
    this.#render()
  }

  // ── Rendering ───────────────────────────────────────────────────────────────

  #render() {
    const { items, total } = this.state[this.activeTab]

    this.#eachField("groups", (el) => {
      el.replaceChildren(...this.#buildGroups(items))
    })
    this.#eachField("empty-all", (el) =>
      el.classList.toggle(
        "hidden",
        !(this.activeTab === "all" && items.length === 0 && !this.loading),
      ),
    )
    this.#eachField("empty-unread", (el) =>
      el.classList.toggle(
        "hidden",
        !(this.activeTab === "unread" && items.length === 0 && !this.loading),
      ),
    )
    this.#eachField("sentinel", (el) =>
      el.classList.toggle("hidden", total <= items.length),
    )

    this.#updateTabs()
    this.#updateBadge()
  }

  #buildGroups(items) {
    const template = this.element.querySelector(
      'template[data-notif-template="group"]',
    )
    const grouped = this.#groupByDate(items)
    return grouped.map(({ label, rows }) => {
      const node = template.content.firstElementChild.cloneNode(true)
      node.querySelector('[data-notif-field="group-label"]').textContent =
        `${label} (${rows.length})`
      const container = node.querySelector('[data-notif-field="group-rows"]')
      container.replaceChildren(...rows.map((item) => this.#buildRow(item)))
      return node
    })
  }

  #buildRow(item) {
    const template = this.element.querySelector(
      'template[data-notif-template="row"]',
    )
    const row = template.content.firstElementChild.cloneNode(true)
    const type = (item.type ?? "generic").replace("::Notification", "")
    const link = deepLinkFor(type, deepLinkInputFor(type, item))

    row.dataset.notifId = item.id
    row.dataset.notifHref = link?.href ?? ""
    row.dataset.notifDownload = String(link?.download ?? false)
    row.dataset.notifRead = String(item.read)
    if (!link) {
      row.classList.remove("cursor-pointer")
      row.removeAttribute("role")
      row.removeAttribute("tabindex")
    }

    // Icon: clone the matching server-rendered SVG (bell fallback).
    const iconSlot = row.querySelector('[data-notif-field="icon"]')
    iconSlot.replaceChildren(this.#iconFor(type))

    row.querySelector('[data-notif-field="message"]').textContent =
      item.message ?? this.labelsValue.new ?? ""
    row.querySelector('[data-notif-field="time"]').textContent =
      formatTimestamp(item.createdAt)

    const dot = row.querySelector('[data-notif-field="unread-dot"]')
    dot.dataset.notifId = item.id
    dot.classList.toggle("hidden", item.read)

    return row
  }

  #iconFor(type) {
    const dict = this.element.querySelector('[data-notif-field="icon-dict"]')
    const node =
      dict?.querySelector(`[data-notif-icon="${CSS.escape(type)}"]`) ??
      dict?.querySelector('[data-notif-icon="__default__"]')
    return node
      ? node.firstElementChild.cloneNode(true)
      : document.createTextNode("")
  }

  #groupByDate(items) {
    const groups = []
    const index = new Map()
    for (const item of items) {
      const key = new Date(item.createdAt).toDateString()
      if (!index.has(key)) {
        const group = { key, label: this.#groupLabel(item.createdAt), rows: [] }
        index.set(key, group)
        groups.push(group)
      }
      index.get(key).rows.push(item)
    }
    return groups
  }

  #groupLabel(iso) {
    const date = new Date(iso)
    const now = new Date()
    const sameDay = (a, b) => a.toDateString() === b.toDateString()
    if (sameDay(date, now)) return this.labelsValue.today ?? "Today"
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    if (sameDay(date, yesterday))
      return this.labelsValue.yesterday ?? "Yesterday"
    const opts =
      date.getFullYear() === now.getFullYear()
        ? { day: "numeric", month: "short" }
        : { day: "numeric", month: "short", year: "numeric" }
    return date.toLocaleDateString(undefined, opts)
  }

  #updateTabs() {
    this.element.querySelectorAll("[data-notif-tab]").forEach((tab) => {
      const type = tab.dataset.notifTab
      const label = tab.dataset.tabLabel
      const total = this.state[type].total
      tab.dataset.active = String(type === this.activeTab)
      const text = tab.querySelector("[data-notif-tab-text]")
      if (text) text.textContent = `${label} (${total})`
    })
  }

  #updateBadge() {
    const hasUnread = this.state.unread.total > 0
    this.#eachField("badge", (el) => el.classList.toggle("hidden", !hasUnread))
  }

  #toggleLoading(on) {
    this.loading = on
    this.#eachField("loading", (el) => el.classList.toggle("hidden", !on))
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  switchTab(event) {
    const tab = event.currentTarget.dataset.notifTab
    if (!tab || tab === this.activeTab) return
    this.activeTab = tab
    this.#render()
  }

  openRow(event) {
    const row = event.currentTarget
    // A key pressed on the row's own controls (the mark-read dot) bubbles up
    // here too; only a key on the row itself activates it. Space would also
    // scroll the list.
    if (event.type === "keydown") {
      if (event.target !== row) return
      event.preventDefault()
    }
    const href = row.dataset.notifHref
    const id = row.dataset.notifId
    if (id && row.dataset.notifRead !== "true") this.#markReadRequest(id)
    if (!href) return
    if (row.dataset.notifDownload === "true") {
      const a = document.createElement("a")
      a.href = href
      a.download = ""
      a.click()
      return
    }
    window.location.assign(href)
  }

  async markRead(event) {
    event.preventDefault()
    event.stopPropagation()
    const id = event.currentTarget.dataset.notifId
    if (!id) return
    await this.#markReadRequest(id)
    this.#applyRead(id)
    this.#render()
  }

  async markAll(event) {
    const button = event.currentTarget
    button.disabled = true
    button.textContent = button.dataset.labelLoading
    try {
      await markAllAsRead()
      this.state.all.items = this.state.all.items.map((n) => ({
        ...n,
        read: true,
      }))
      this.state.unread = { items: [], total: 0 }
      this.#render()
    } finally {
      button.disabled = false
      button.textContent = button.dataset.labelDefault
    }
  }

  closeMobile() {
    const dialog = this.element.querySelector(
      "dialog[data-controller~='ui--modal']",
    )
    const modal = dialog
      ? this.application.getControllerForElementAndIdentifier(
          dialog,
          "ui--modal",
        )
      : null
    modal?.close?.()
  }

  async #markReadRequest(id) {
    try {
      await markAsRead(id)
    } catch {
      /* optimistic — ignore transient failure */
    }
  }

  #applyRead(id) {
    this.state.all.items = this.state.all.items.map((n) =>
      n.id === id ? { ...n, read: true } : n,
    )
    const wasUnread = this.state.unread.items.some((n) => n.id === id)
    this.state.unread.items = this.state.unread.items.filter((n) => n.id !== id)
    if (wasUnread)
      this.state.unread.total = Math.max(0, this.state.unread.total - 1)
  }

  // ── Infinite scroll ───────────────────────────────────────────────────────────

  #observeSentinels() {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.loading) return
        if (entries.some((e) => e.isIntersecting)) this.#loadMore()
      },
      { rootMargin: "100px" },
    )
    this.#eachField("sentinel", (el) => this.observer.observe(el))
  }

  async #loadMore() {
    const tab = this.activeTab
    const { items, total } = this.state[tab]
    if (total <= items.length || this.loading) return
    this.loading = true
    this.takes[tab] += PAGE_STEP
    await this.#refetch(tab)
    this.loading = false
  }

  // ── Realtime + poll ───────────────────────────────────────────────────────────

  #subscribe() {
    const consumer = getConsumer("default")
    this.subscription = consumer.subscriptions.create(
      { channel: CHANNEL },
      {
        received: (data) => {
          if (data?.type === NOTIFICATIONS_CHANNEL_TYPE)
            this.#onNewNotification()
        },
      },
    )
  }

  #onNewNotification() {
    // Mirror the React refetch-on-new behaviour: refresh both tabs so the badge
    // and the active list update.
    this.#refetch("unread")
    if (this.activeTab === "all") this.#refetch("all")
  }

  #startPoll() {
    this.pollTimer = setInterval(() => {
      this.#refetch("unread")
      this.#refetch("all")
    }, this.pollIntervalValue)
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  #eachField(name, fn) {
    this.element.querySelectorAll(`[data-notif-field="${name}"]`).forEach(fn)
  }
}
